/**
 * TargetGroupOverlayContent Component
 * Displays detailed Target Group (Zielgruppe) data in the overlay
 * Styled to match VertriebsgebietOverlayContent
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, GraduationCap, Briefcase, CreditCard, Target, FileText, User } from 'lucide-react';
import { AnalysisChart } from '@/components/charts/AnalysisChart/AnalysisChart';
import type { ZielgruppeFullData } from '@/types/zielgruppe';
import type { ChartConfig } from '@/types/api';
import { SectionDescriptionText } from '@/components/results/SectionDescriptionText';
import type { GetDescriptionFn } from '@/types/sectionDescription';

export interface TargetGroupOverlayContentProps {
  data: ZielgruppeFullData | string;
  getDescription?: GetDescriptionFn;
  featureName?: string;
}

// Color palette matching the brand
const CHART_COLORS = [
  '#FC03B0', // Primary pink (brand start)
  '#FF6803', // Primary orange (brand end)
  '#E8148A', // Darker pink
  '#FF8533', // Lighter orange
  '#D02090', // Deep magenta
  '#FFB366', // Soft orange
  '#A01870', // Muted magenta
  '#CC5200', // Deep orange
];

/**
 * Overlay content for Target Group with full details
 */
export function TargetGroupOverlayContent({
  data,
  getDescription,
  featureName,
}: TargetGroupOverlayContentProps) {
  const { t } = useTranslation();

  // Handle legacy string data
  if (typeof data === 'string') {
    return (
      <div className="space-y-6">
        {/* Header with icon and title */}
        <div className="hidden md:flex items-center gap-2 md:gap-3 pb-1 md:pb-2">
          <div className="p-2 md:p-2.5 rounded-lg bg-gradient-to-br from-[var(--color-primary-start)]/10 to-[var(--color-primary-end)]/10">
            <Users className="h-5 w-5 md:h-6 md:w-6 text-[var(--color-primary-start)]" />
          </div>
          <h3 className="text-base md:text-lg font-semibold leading-none tracking-tight text-[var(--color-charcoal)]">{t('results.cards.targetGroup.title')}</h3>
        </div>

        {/* Main content section */}
        <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
          <p className="text-sm text-[var(--color-charcoal)]/80 leading-relaxed whitespace-pre-wrap">
            {data}
          </p>
        </div>
      </div>
    );
  }

  const { main, personas } = data;

  // Create pie chart config for Altersspektrum
  const altersspektrumChartConfig = useMemo((): ChartConfig | null => {
    const altersspektrum = main.Zielgruppe_Altersspektrum_array;
    if (!altersspektrum || altersspektrum.length === 0) {
      return null;
    }

    return {
      type: 'pie',
      series: altersspektrum.map(a => a.Zielgruppe_Altersspektrum_item_prozent) as unknown as ChartConfig['series'],
      options: {
        labels: altersspektrum.map(a => a.Zielgruppe_Altersspektrum_item_name),
        colors: CHART_COLORS.slice(0, altersspektrum.length),
        legend: {
          show: true,
          position: 'bottom',
        },
        dataLabels: {
          enabled: true,
          formatter: (val: number) => `${val.toFixed(0)}%`,
        },
        tooltip: {
          y: {
            formatter: (val: number) => `${val}%`,
          },
        },
        stroke: {
          width: 2,
          colors: ['#fff'],
        },
      } as ChartConfig['options'],
    };
  }, [main.Zielgruppe_Altersspektrum_array]);

  // Create bar chart config for Bildungscluster
  const bildungsclusterChartConfig = useMemo((): ChartConfig | null => {
    const bildungscluster = main.Zielgruppe_Bildungscluster_array;
    if (!bildungscluster || bildungscluster.length === 0) {
      return null;
    }

    return {
      type: 'bar',
      series: [{
        name: 'Anteil',
        data: bildungscluster.map(b => b.Zielgruppe_Bildungscluster_item_prozent),
      }] as unknown as ChartConfig['series'],
      options: {
        chart: {
          type: 'bar',
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: '60%',
          },
        },
        colors: ['#FC03B0'],
        xaxis: {
          categories: bildungscluster.map(b => b.Zielgruppe_Bildungscluster_item_name),
          labels: {
            style: {
              fontSize: '11px',
            },
          },
        },
        yaxis: {
          labels: {
            formatter: (val: number) => `${val}%`,
          },
        },
        dataLabels: {
          enabled: true,
          formatter: (val: number) => `${val}%`,
        },
        tooltip: {
          y: {
            formatter: (val: number) => `${val}%`,
          },
        },
      } as ChartConfig['options'],
    };
  }, [main.Zielgruppe_Bildungscluster_array]);

  const personaDetails = main.Zielgruppe_Persona_details;

  return (
    <div className="space-y-6">
      {/* Header with icon and title */}
      <div className="hidden md:flex items-center gap-2 md:gap-3 pb-1 md:pb-2">
        <div className="p-2 md:p-2.5 rounded-lg bg-gradient-to-br from-[var(--color-primary-start)]/10 to-[var(--color-primary-end)]/10">
          <Users className="h-5 w-5 md:h-6 md:w-6 text-[var(--color-primary-start)]" />
        </div>
        <h3 className="text-base md:text-lg font-semibold leading-none tracking-tight text-[var(--color-charcoal)]">{t('results.cards.targetGroup.title')}</h3>
      </div>

      {/* Personas section - Top 3 with larger images */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <h4 className="text-lg font-semibold text-[var(--color-charcoal)]">{t('results.cards.targetGroup.overlay.top3Personas')}</h4>
          <SectionDescriptionText getDescription={getDescription} featureName={featureName} sectionKey="top3Personas" />
        </div>
        <div className="grid grid-cols-3 gap-6">
          {personas.map((persona, index) => (
            <div key={index} className="text-center p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
              {/* Ranking badge */}
              <div className="w-8 h-8 mx-auto mb-2 rounded-full bg-gradient-to-br from-[var(--color-primary-start)] to-[var(--color-primary-end)] flex items-center justify-center">
                <span className="text-white font-bold text-sm">{index + 1}</span>
              </div>
              {/* Percentage */}
              <p className="text-3xl font-bold text-[var(--color-primary-start)]">
                {persona.prozent}%
              </p>
              {/* Name */}
              <p className="font-semibold text-[var(--color-charcoal)] mt-1">
                {persona.name}
              </p>
              {/* Description */}
              {persona.beschreibung && (
                <p className="text-xs text-[var(--color-charcoal)]/70 mt-2 leading-relaxed">
                  {persona.beschreibung}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Age Spectrum and Education Cluster - stacked on mobile */}
      <div className="pt-4 border-t border-[var(--glass-border)]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {/* Altersspektrum Pie Chart */}
          <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
            <div className="flex items-center gap-2 mb-3">
              <Target className="h-5 w-5 text-[var(--color-primary-start)]" />
              <span className="text-sm font-medium text-[var(--color-charcoal)]">{t('results.cards.targetGroup.overlay.ageSpectrum')}</span>
            </div>
            {altersspektrumChartConfig ? (
              <div className="flex flex-col items-center">
                <AnalysisChart
                  config={altersspektrumChartConfig}
                  height={280}
                />
                <p className="text-sm text-[var(--color-charcoal)]/70 mt-2">
                  <span className="font-medium">{t('results.cards.targetGroup.overlay.medianAge')}</span> {main.Zielgruppe_Altersspektrum_median} {t('results.cards.targetGroup.overlay.years')}
                </p>
              </div>
            ) : (
              <div className="h-[280px] flex items-center justify-center">
                <span className="text-sm text-[var(--color-charcoal)]/50">{t('results.cards.targetGroup.overlay.noDataAvailable')}</span>
              </div>
            )}
          </div>

          {/* Bildungscluster Bar Chart */}
          <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
            <div className="flex items-center gap-2 mb-3">
              <GraduationCap className="h-5 w-5 text-[var(--color-primary-start)]" />
              <span className="text-sm font-medium text-[var(--color-charcoal)]">{t('results.cards.targetGroup.overlay.educationCluster')}</span>
            </div>
            {bildungsclusterChartConfig ? (
              <AnalysisChart
                config={bildungsclusterChartConfig}
                height={280}
              />
            ) : (
              <div className="h-[280px] flex items-center justify-center">
                <span className="text-sm text-[var(--color-charcoal)]/50">{t('results.cards.targetGroup.overlay.noDataAvailable')}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Persona Details section */}
      {personaDetails && personaDetails.Name && (
        <div className="pt-4 border-t border-[var(--glass-border)]">
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-lg font-semibold text-[var(--color-charcoal)]">{t('results.cards.targetGroup.overlay.personaDetails')}</h4>
            <SectionDescriptionText getDescription={getDescription} featureName={featureName} sectionKey="personaDetails" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {/* Basic info */}
            <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
              <div className="flex items-center gap-2 mb-3">
                <User className="h-5 w-5 text-[var(--color-primary-start)]" />
                <span className="text-sm font-medium text-[var(--color-charcoal)]">{t('results.cards.targetGroup.overlay.profile')}</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[var(--color-charcoal)]/70">{t('results.cards.targetGroup.overlay.name')}</span>
                  <span className="font-medium text-[var(--color-charcoal)]">{personaDetails.Name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--color-charcoal)]/70">{t('results.cards.targetGroup.overlay.age')}</span>
                  <span className="font-medium text-[var(--color-charcoal)]">{personaDetails.Alter} {t('results.cards.targetGroup.overlay.years')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--color-charcoal)]/70">{t('results.cards.targetGroup.overlay.gender')}</span>
                  <span className="font-medium text-[var(--color-charcoal)]">{personaDetails.Geschlecht}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--color-charcoal)]/70">{t('results.cards.targetGroup.overlay.householdForm')}</span>
                  <span className="font-medium text-[var(--color-charcoal)]">{personaDetails.Haushaltsform}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--color-charcoal)]/70">{t('results.cards.targetGroup.overlay.lifePhase')}</span>
                  <span className="font-medium text-[var(--color-charcoal)]">{personaDetails.Lebensphase}</span>
                </div>
              </div>
            </div>

            {/* Professional info */}
            <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
              <div className="flex items-center gap-2 mb-3">
                <Briefcase className="h-5 w-5 text-[var(--color-primary-end)]" />
                <span className="text-sm font-medium text-[var(--color-charcoal)]">{t('results.cards.targetGroup.overlay.professional')}</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[var(--color-charcoal)]/70">{t('results.cards.targetGroup.overlay.educationLevel')}</span>
                  <span className="font-medium text-[var(--color-charcoal)] text-right">{personaDetails.Bildungsniveau}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--color-charcoal)]/70">{t('results.cards.targetGroup.overlay.professionalStatus')}</span>
                  <span className="font-medium text-[var(--color-charcoal)] text-right">{personaDetails.Berufsstatus}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--color-charcoal)]/70">{t('results.cards.targetGroup.overlay.income')}</span>
                  <span className="font-medium text-[var(--color-charcoal)] text-right">{personaDetails.Einkommen?.toLocaleString('de-DE')} €</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--color-charcoal)]/70">{t('results.cards.targetGroup.overlay.relevance')}</span>
                  <span className="font-medium text-[var(--color-charcoal)] text-right">{personaDetails.Relevanz}</span>
                </div>
              </div>
            </div>

            {/* Kaufmotive */}
            <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
              <div className="flex items-center gap-2 mb-3">
                <Target className="h-5 w-5 text-[var(--color-primary-start)]" />
                <span className="text-sm font-medium text-[var(--color-charcoal)]">{t('results.cards.targetGroup.overlay.purchaseMotives')}</span>
              </div>
              <p className="text-sm text-[var(--color-charcoal)]/80 leading-relaxed">
                {personaDetails.Kaufmotive}
              </p>
            </div>

            {/* Painpoints */}
            <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
              <div className="flex items-center gap-2 mb-3">
                <CreditCard className="h-5 w-5 text-[var(--color-primary-end)]" />
                <span className="text-sm font-medium text-[var(--color-charcoal)]">{t('results.cards.targetGroup.overlay.painpoints')}</span>
              </div>
              <p className="text-sm text-[var(--color-charcoal)]/80 leading-relaxed">
                {personaDetails.Painpoints}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Zahlungsbereitschaft section */}
      {(main.Zielgruppe_Zahlungsbereitschaft_Zahlungsmodell || main.Zielgruppe_Zahlungsbereitschaft_Rabatte || main.Zielgruppe_Zahlungsbereitschaft_Zahlungszeitpunkte) && (
        <div className="pt-4 border-t border-[var(--glass-border)]">
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-lg font-semibold text-[var(--color-charcoal)]">{t('results.cards.targetGroup.overlay.paymentWillingness')}</h4>
            <SectionDescriptionText getDescription={getDescription} featureName={featureName} sectionKey="paymentWillingness" />
          </div>
          <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[var(--color-charcoal)]/70">{t('results.cards.targetGroup.overlay.paymentModel')}</span>
                <span className="font-medium text-[var(--color-charcoal)]">{main.Zielgruppe_Zahlungsbereitschaft_Zahlungsmodell}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--color-charcoal)]/70">{t('results.cards.targetGroup.overlay.discounts')}</span>
                <span className="font-medium text-[var(--color-charcoal)]">{main.Zielgruppe_Zahlungsbereitschaft_Rabatte}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--color-charcoal)]/70">{t('results.cards.targetGroup.overlay.paymentTiming')}</span>
                <span className="font-medium text-[var(--color-charcoal)]">{main.Zielgruppe_Zahlungsbereitschaft_Zahlungszeitpunkte}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary section */}
      {main.Zielgruppe_Zusammenfassung && (
        <div className="pt-4 border-t border-[var(--glass-border)]">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="h-5 w-5 text-[var(--color-primary-start)]" />
            <h4 className="text-lg font-semibold text-[var(--color-charcoal)]">{t('results.cards.targetGroup.overlay.summary')}</h4>
            <SectionDescriptionText getDescription={getDescription} featureName={featureName} sectionKey="summary" />
          </div>
          <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
            <p className="text-sm text-[var(--color-charcoal)]/80 leading-relaxed">
              {main.Zielgruppe_Zusammenfassung}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default TargetGroupOverlayContent;
